import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import {
  mapFundboxEligibilityStatus,
  mapFundboxExpirationDate,
  parseFundboxCreditAmount,
  selectFundboxLendingOpportunity,
} from '@/enterprise/data/fundboxOpportunityMappers';
import apiClient from '@/lib/apiClient';
import useDataDog from '@/reporting/DataDog';
import useLocaleFormat from '@/utils/useLocaleFormat';

type LendingOpportunitiesResponse =
  paths['/user/lending-opportunities']['get']['responses'][200]['content']['application/json'];

export const FUNDBOX_LENDING_OPPORTUNITIES_QUERY_KEY = ['lending-opportunities', 'fundbox'] as const;

export const fetchUserLendingOpportunities = async (): Promise<LendingOpportunitiesResponse['data']> => {
  const response = await apiClient
    .get('api/supplier-experience/user/lending-opportunities')
    .json<LendingOpportunitiesResponse>();

  return response.data;
};

export interface FundboxOpportunityFields {
  status: ReturnType<typeof mapFundboxEligibilityStatus>;
  displayAmount: string | undefined;
  expirationDate: string | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Returns Fundbox opportunity fields for page content (headline, supporting line, CTA, benefits).
 * Does not gate nav or route access — consumers use `useNavigationArtifact(NAVIGATION_CONFIG_KEY.FUNDBOX)`.
 *
 * `expirationDate` is read from `GET /user/lending-opportunities` (`YYYY-MM-DD`) and formatted as `MM/DD/YYYY`.
 * `displayAmount` is read from `providerDetails.amount` and formatted via `asCurrency` for pre-qualified marketing copy.
 */
const useFundboxOpportunity = (): FundboxOpportunityFields => {
  const { asCurrency } = useLocaleFormat();
  const { addAction } = useDataDog();
  const {
    data: opportunity,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: FUNDBOX_LENDING_OPPORTUNITIES_QUERY_KEY,
    queryFn: fetchUserLendingOpportunities,
    throwOnError: false,
    select: (data) => selectFundboxLendingOpportunity(data.opportunities),
  });

  const creditAmount = parseFundboxCreditAmount(opportunity?.providerDetails?.amount);

  const refetchOpportunity = useCallback((): void => {
    if (isError) {
      addAction('fundbox-opportunity::refetch-after-error', { feature: 'fundbox' });
    }

    void refetch();
  }, [addAction, isError, refetch]);

  return {
    status: mapFundboxEligibilityStatus(opportunity),
    displayAmount: creditAmount ? asCurrency(creditAmount, 'USD') : undefined,
    expirationDate: mapFundboxExpirationDate(opportunity),
    isPending,
    isError,
    refetch: refetchOpportunity,
  };
};

export default useFundboxOpportunity;
